import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useOnboarding } from "@/context/OnboardingContext";
import { useLang } from "@/context/LanguageContext";
import LanguagePill from "@/components/LanguagePill";
import OnboardingExit from "@/components/OnboardingExit";

export default function OnboardingDeepDive() {
  const { idx } = useParams();
  const i = parseInt(idx, 10);
  const navigate = useNavigate();
  const { data, update } = useOnboarding();
  const { t, lang } = useLang();
  const [options, setOptions] = useState({ priorities: [], heart_options: {} });
  const [rating, setRating] = useState(null);
  const [heart, setHeart] = useState("");
  const [note, setNote] = useState("");

  const priorityKey = data.priorities?.[i];
  const priorityLabel = options.priorities.find(p => p.key === priorityKey)?.label || priorityKey;
  const heartOpts = options.heart_options[priorityKey] || [];

  useEffect(() => {
    api.get("/onboarding/options").then(r => setOptions(r.data)).catch(() => {});
  }, [lang]);

  useEffect(() => {
    const existing = data.deep_dives?.find(d => d.priority === priorityKey);
    if (existing) {
      setRating(existing.rating);
      setHeart(existing.heart);
      setNote(existing.note || "");
    } else {
      setRating(null); setHeart(""); setNote("");
    }
  // eslint-disable-next-line
  }, [priorityKey]);

  if (!priorityKey) return null;

  const next = () => {
    const newDive = { priority: priorityKey, rating, heart, note };
    const others = (data.deep_dives || []).filter(d => d.priority !== priorityKey);
    update({ deep_dives: [...others, newDive] });
    if (i + 1 < data.priorities.length) {
      navigate(`/onboarding/deep-dive/${i + 1}`);
    } else {
      navigate("/onboarding/life-letter");
    }
  };

  return (
    <div className="min-h-screen px-6 py-10 max-w-md mx-auto" style={{ backgroundColor: "var(--vedari-cream)", color: "var(--vedari-ink)" }}>
      <div className="flex items-center justify-between mb-2 gap-3">
        <p className="text-caption" style={{ color: "var(--vedari-ink-soft)" }}>
          <span className="key-data">{i + 1}</span> {t("ob.dive.of")} <span className="key-data">{data.priorities.length}</span>
        </p>
        <LanguagePill persist />
        <OnboardingExit />
      </div>
      <h1 className="font-display text-3xl mt-2 mb-1 leading-tight" data-testid="deepdive-title">{priorityLabel}</h1>
      <p className="text-body" style={{ color: "var(--vedari-ink-soft)" }}>
        {t("ob.dive.sub")}
      </p>

      <div className="space-y-8 mt-8">
        <div>
          <label className="block text-body-sm font-medium mb-3" style={{ color: "var(--vedari-ink-soft)" }}>
            {t("ob.dive.rate_label")}
          </label>
          <div className="flex items-center gap-2">
            {[1,2,3,4,5].map(v => (
              <button key={v} type="button" onClick={() => setRating(v)} data-testid={`rating-${v}`}
                className="flex-1 py-3 rounded-md transition-all text-body-sm font-medium"
                style={{
                  backgroundColor: rating === v ? "var(--vedari-gold)" : "var(--vedari-surface)",
                  color: rating === v ? "var(--vedari-cream)" : "var(--vedari-ink)",
                  border: `1px solid ${rating === v ? "var(--vedari-gold)" : "var(--vedari-divider)"}`,
                }}>
                {v}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-caption mt-2" style={{ color: "var(--vedari-ink-soft)" }}>
            <span>{t("ob.dive.rate_low")}</span><span>{t("ob.dive.rate_high")}</span>
          </div>
        </div>

        <div>
          <label className="block text-body-sm font-medium mb-3" style={{ color: "var(--vedari-ink-soft)" }}>
            {t("ob.dive.heart_label")}
          </label>
          <div className="space-y-2">
            {heartOpts.map(opt => (
              <button key={opt} type="button" onClick={() => setHeart(opt)} data-testid={`heart-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                className="w-full text-left p-4 rounded-xl transition-all text-body"
                style={{
                  backgroundColor: heart === opt ? "rgba(184,149,74,0.10)" : "var(--vedari-surface)",
                  border: `1.5px solid ${heart === opt ? "var(--vedari-gold)" : "var(--vedari-divider)"}`,
                  color: "var(--vedari-ink)",
                }}>
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-body-sm font-medium mb-3" style={{ color: "var(--vedari-ink-soft)" }}>
            {t("ob.dive.note_label")} <span style={{ opacity: 0.5 }}>{t("ob.dive.note_opt")}</span>
          </label>
          <textarea className="vedari-input min-h-[100px] resize-none" maxLength={200}
            value={note} onChange={e => setNote(e.target.value)}
            placeholder={t("ob.dive.note_ph")}
            data-testid="deepdive-note" />
          <p className="text-caption text-right mt-1" style={{ color: "var(--vedari-ink-soft)" }}>{note.length}/200</p>
        </div>
      </div>

      <div className="flex gap-3 mt-8">
        <button onClick={() => i > 0 ? navigate(`/onboarding/deep-dive/${i-1}`) : navigate("/onboarding/priorities")} className="vedari-btn-secondary flex-1" data-testid="deepdive-back-btn">{t("common.back")}</button>
        <button onClick={next} disabled={!heart || !rating} className="vedari-btn-primary flex-1" data-testid="deepdive-continue-btn">{t("common.continue")}</button>
      </div>
    </div>
  );
}
